import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '@diggens/api-client';

interface Connection {
  id: string;
  label: string;
  domain: string;
  collectDocuments: boolean;
  collectionOwnerId: string | null;
  connectedAt: string;
}

interface Owner {
  id: string;
  name: string;
  email: string;
}

interface Credentials {
  domain: string;
  authUrl: string;
  clientId: string;
  clientSecret: string;
}

const FREYDA_QUERY_KEY = ['integrations', 'freyda'] as const;
const EMPTY_CREDENTIALS: Credentials = { domain: '', authUrl: '', clientId: '', clientSecret: '' };

/** The API's own words for a change it refused (a duplicate domain or name, a credential Freyda rejected), else `fallback`. */
function refusal(error: unknown, fallback: string): string {
  return error instanceof ApiError && (error.status === 409 || error.status === 422) ? error.message : fallback;
}

function complete(values: object): boolean {
  return Object.values(values).every((value) => typeof value !== 'string' || value.trim() !== '');
}

/** The four credential fields, shared by the add form and a connection's replace form. */
function CredentialFields({ values, onChange, domainLocked = false }: { values: Credentials; onChange: (next: Credentials) => void; domainLocked?: boolean }) {
  const field = (key: keyof Credentials) => ({ value: values[key], onChange: (event: { target: { value: string } }) => onChange({ ...values, [key]: event.target.value }) });
  return (
    <>
      <TextField label="Freyda domain" placeholder="acme.staging.ireland.freyda.io" size="small" autoComplete="off" disabled={domainLocked} {...field('domain')} />
      <TextField label="Token URL" placeholder="https://acme.auth.eu-west-1.amazoncognito.com" size="small" autoComplete="off" {...field('authUrl')} />
      <TextField label="Client ID" size="small" autoComplete="off" {...field('clientId')} />
      <TextField label="Client secret" type="password" size="small" autoComplete="new-password" {...field('clientSecret')} />
    </>
  );
}

/** One connection: its name, domain, whether it is in scope for collection, and renaming, new credentials or removal. */
function ConnectionRow({ connection, owners }: { connection: Connection; owners: Owner[] }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<'none' | 'rename' | 'credentials'>('none');
  const [label, setLabel] = useState(connection.label);
  const [credentials, setCredentials] = useState<Credentials>({ ...EMPTY_CREDENTIALS, domain: connection.domain });
  const refresh = () => queryClient.invalidateQueries({ queryKey: FREYDA_QUERY_KEY });

  const update = useMutation({
    mutationFn: (change: { label?: string; collectDocuments?: boolean; collectionOwnerId?: string | null; credentials?: Credentials }) =>
      apiFetch<Connection>(`/integrations/freyda/${connection.id}`, { method: 'PATCH', body: JSON.stringify(change) }),
    onSuccess: () => {
      setEditing('none');
      setCredentials({ ...EMPTY_CREDENTIALS, domain: connection.domain });
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiFetch<void>(`/integrations/freyda/${connection.id}`, { method: 'DELETE' }),
    onSettled: () => void refresh(),
  });

  const connectedAt = new Date(connection.connectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const removeConnection = () => {
    if (window.confirm(`Remove the Freyda connection "${connection.label}"? Diggens stops working in that Freyda client.`)) remove.mutate();
  };

  return (
    <Box data-testid={`freyda-connection-${connection.id}`} sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography fontWeight={600}>{connection.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {connection.domain} · connected on {connectedAt}
          </Typography>
          <FormControlLabel
            control={<Switch size="small" checked={connection.collectDocuments} disabled={update.isPending} onChange={(event) => update.mutate({ collectDocuments: event.target.checked })} />}
            label="In-scope: Operational Management (Collect)"
          />
          {connection.collectDocuments && (
            <TextField
              select
              size="small"
              label="Collection owner"
              sx={{ mt: 1, minWidth: 240, display: 'block' }}
              value={connection.collectionOwnerId ?? ''}
              disabled={update.isPending}
              onChange={(event) => update.mutate({ collectionOwnerId: event.target.value === '' ? null : event.target.value })}
              helperText="The person collection through this connection is done for."
            >
              <MenuItem value="">Nobody</MenuItem>
              {owners.map((owner) => (
                <MenuItem key={owner.id} value={owner.id}>
                  {owner.name} ({owner.email})
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setEditing(editing === 'rename' ? 'none' : 'rename')}>
            Rename
          </Button>
          <Button size="small" onClick={() => setEditing(editing === 'credentials' ? 'none' : 'credentials')}>
            Replace credentials
          </Button>
          <Button size="small" color="error" onClick={removeConnection} disabled={remove.isPending}>
            Remove
          </Button>
        </Stack>
      </Box>

      {update.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {refusal(update.error, 'Could not update the connection. Try again.')}
        </Alert>
      )}
      {remove.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not remove the connection. Try again.
        </Alert>
      )}

      {editing === 'rename' && (
        <Box component="form" sx={{ mt: 2, display: 'flex', gap: 1 }} onSubmit={(event: FormEvent) => (event.preventDefault(), label.trim() && update.mutate({ label }))}>
          <TextField label="Name" size="small" value={label} onChange={(event) => setLabel(event.target.value)} inputProps={{ maxLength: 80 }} />
          <Button type="submit" variant="contained" size="small" disabled={!label.trim() || update.isPending}>
            Save
          </Button>
        </Box>
      )}
      {editing === 'credentials' && (
        <Box component="form" sx={{ mt: 2 }} onSubmit={(event: FormEvent) => (event.preventDefault(), complete(credentials) && update.mutate({ credentials }))}>
          <Stack spacing={2}>
            <CredentialFields values={credentials} onChange={setCredentials} domainLocked />
            <Box>
              <Button type="submit" variant="contained" size="small" disabled={!complete(credentials) || update.isPending}>
                {update.isPending ? 'Checking…' : 'Save credentials'}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/**
 * Settings > Connections card where tenant admins connect the firm's Freyda service accounts, one per Freyda client
 * domain (a client in one environment), each under a name, and mark the ones in scope for collection. The API checks
 * each credential against Freyda before saving it and never sends a secret back; a form clears it once it is saved.
 */
export function FreydaConnection() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [credentials, setCredentials] = useState<Credentials>(EMPTY_CREDENTIALS);

  const list = useQuery({ queryKey: FREYDA_QUERY_KEY, queryFn: () => apiFetch<{ connections: Connection[]; owners: Owner[] }>('/integrations/freyda') });

  const connect = useMutation({
    mutationFn: (body: Credentials & { label: string }) => apiFetch<Connection>('/integrations/freyda', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setLabel('');
      setCredentials(EMPTY_CREDENTIALS);
      setAdding(false);
      void queryClient.invalidateQueries({ queryKey: FREYDA_QUERY_KEY });
    },
  });

  if (list.isPending) {
    return (
      <Box>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const connections = list.data?.connections ?? [];
  const showForm = adding || (list.isSuccess && connections.length === 0);
  const ready = label.trim() !== '' && complete(credentials);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) connect.mutate({ label, ...credentials });
  };

  return (
    <Card sx={{ maxWidth: 800 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Freyda
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Connect each Freyda client your firm uses, one per environment, with a Freyda service account. Mark the ones in scope for
          Operational Management (Collect), and choose who owns collection for each.
        </Typography>

        {list.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load the Freyda connections.
          </Alert>
        )}

        {connections.map((connection, index) => (
          <Box key={connection.id}>
            {index > 0 && <Divider />}
            <ConnectionRow connection={connection} owners={list.data?.owners ?? []} />
          </Box>
        ))}

        {connections.length > 0 && !adding && (
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" onClick={() => setAdding(true)}>
              Add a Freyda connection
            </Button>
          </Box>
        )}

        {showForm && (
          <Box component="form" onSubmit={submit} sx={{ mt: connections.length > 0 ? 2 : 0 }}>
            {connections.length > 0 && <Divider sx={{ mb: 2 }} />}
            {connect.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {refusal(connect.error, 'Could not connect Freyda. Try again.')}
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField label="Name" placeholder="Acme (staging)" size="small" autoComplete="off" value={label} onChange={(event) => setLabel(event.target.value)} inputProps={{ maxLength: 80 }} />
              <CredentialFields values={credentials} onChange={setCredentials} />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={!ready || connect.isPending}>
                  {connect.isPending ? 'Checking…' : 'Connect Freyda'}
                </Button>
                {connections.length > 0 && (
                  <Button onClick={() => setAdding(false)} disabled={connect.isPending}>
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        )}

      </CardContent>
    </Card>
  );
}
